import { onMounted, computed, watch, ref } from 'vue';
import { of, merge } from 'rxjs';
import { catchError, filter, map, tap, switchMap, debounceTime } from 'rxjs/operators';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
import { axiosCallerPlugin } from 'src/plugins/axios/componentAxiosCallerPlugin';
import { AppGeneralVariables } from 'src/plugins/general/variables';
import { useRoute, useRouter } from 'vue-router';
import { useAccountStore } from 'src/stores/account';
import { useAppModeStore } from 'src/stores/appMode';
import { useConvoStore } from 'src/stores/conversations';
import { useLoggingStore } from 'src/stores/logging';
import { useViewStore } from 'src/stores/view';
/**
 * Query chat on app load
 */
const QueryChatPlugin = () => {
    const vueRoute = useRoute();
    const vueRouter = useRouter();
    const accountStore = useAccountStore();
    const appModeStore = useAppModeStore();
    const convoStore = useConvoStore();
    const loggingStore = useLoggingStore();
    const viewStore = useViewStore();
    const { styleType } = AppGeneralVariables();
    const firebaseQuery = ref(false);
    const firebaseLoggedIn = computed(() => {
        return accountStore.firebaseLoggedIn;
    });
    const user = computed(() => accountStore.user);
    /*****
     * Query for chat info in backend if chatId is present in url query params.
     * Then, using the information, open the chat box
     *****/
    const { subject: queryChat$ } = rxInitSubjectCallback();
    const { subject: userWatcher$ } = rxInitSubjectCallback();
    const { axiosCall } = axiosCallerPlugin();
    useObservable(merge(queryChat$, userWatcher$.pipe(filter(({ newValue }) => {
        return newValue !== null;
    }))).pipe(debounceTime(800), map(() => {
        return vueRoute.query.chatId;
    }), filter((v) => v !== null), filter((v) => v !== undefined), filter(() => {
        if (!user.value) {
            viewStore.requireLogin({
                required: true
            });
            return false;
        }
        return true;
    }), filter(() => firebaseLoggedIn.value), filter(() => !firebaseQuery.value), switchMap((v) => {
        return of('').pipe(switchMap(() => {
            firebaseQuery.value = true;
            return axiosCall({
                type: 'create',
                loading: false,
                data: {
                    firebase_chat_id_list: [v],
                },
                url: 'chat_room/inbox_info',
                urlType: 'chat',
                state: 'inboxUpdater',
            });
        }), catchError((err) => {
            loggingStore.errorHandler(err);
            return of({
                success: false,
            });
        }), map((v) => v), filter((v) => v.success), filter(({ data }) => data !== null), map(({ data }) => data), map(({ results }) => results), filter((v) => v.length > 0), map((v) => v[0]), tap((data) => {
            const receiverDetails = data.receiver_detail.receiver_pk !== user.value?.user_pk
                ? {
                    id: data.receiver_detail.receiver_pk,
                    name: data.receiver_detail.receiver_name,
                    pic: data.receiver_detail.receiver_profile_pic,
                } : {
                id: data.sender_detail.sender_pk,
                name: data.sender_detail.sender_name,
                pic: data.sender_detail.sender_profile_pic,
            };
            // set chat box with info
            convoStore.setCurrentConversation({
                id: data.id,
                listingName: data.space_detail.space_name,
                receiverPic: receiverDetails.pic,
                receiverName: receiverDetails.name,
            });
        }), tap(() => {
            /*******
             * Open chat box
             ******/
            // show chat in drawer
            if (styleType.value === 'desktop') {
                viewStore.toggleInboxMenu(false);
                viewStore.toggleDrawer(true);
                viewStore.toggleDrawerTab('chat');
                return;
            }
            // go to chat page for mobile and app
            void vueRouter.push({ name: 'Inbox' });
            if (styleType.value === 'app') {
                appModeStore.toggleShowChatDialog(true);
                return;
            }
            viewStore.toggleChatTab('chat');
        }));
    })));
    watch(user, (newValue, oldValue) => {
        userWatcher$.next({ newValue, oldValue });
    });
    watch(firebaseLoggedIn, (newValue, oldValue) => {
        if (newValue && !oldValue) {
            queryChat$.next(vueRoute.query.chatId);
        }
    });
    onMounted(() => {
        queryChat$.next(vueRoute.query.chatId);
    });
};
export { QueryChatPlugin };
